/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, ShieldAlert, CheckCircle2, X } from "lucide-react";
import { useLang } from "./i18n";
import { ChangesTable, MovementsCard, AnomaliesCard, AuditLog } from "./CeoAiCards";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Trouve toutes les anomalies (stock négatif, arrondis, écarts)",
  "Montre l'historique des mouvements de stock de Test Stocker",
  "Recalcule Jean Marc",
  "Réinitialise le code secret de Fallone",
];

export default function CeoAiAssistant({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null); // {tool, args, summary, session_id}
  const [executing, setExecuting] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [tab, setTab] = useState("chat");
  const { lang } = useLang();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending]);

  const sendMessage = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    const nextHistory = [...messages, { role: "user", content: msg }];
    setMessages(nextHistory);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ceo/ai-assistant/chat`, {
        message: msg,
        history: messages.filter((m) => m.role !== "card"),
        session_id: sessionId,
        lang,
      });
      const { assistant_text, pending_action, session_id, tool_traces } = r.data || {};
      if (session_id) setSessionId(session_id);
      const cards = (tool_traces || []).filter((t) => (t.tool === "get_stock_movements" && t.result?.user) || (t.tool === "find_anomalies" && t.result?.anomalies))
        .map((t) => ({ role: "card", kind: t.tool, data: t.result }));
      setMessages([...nextHistory, ...cards, { role: "assistant", content: assistant_text || "…" }]);
      if (pending_action) setPending(pending_action);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur IA");
      setMessages([...nextHistory, { role: "assistant", content: "Erreur technique — réessayez." }]);
    } finally {
      setLoading(false);
    }
  };

  const confirmAction = async () => {
    if (!pending) return;
    setExecuting(true);
    try {
      const r = await axios.post(`${API}/ceo/ai-assistant/execute`, {
        tool: pending.tool,
        args: pending.args,
      });
      toast.success(r.data?.message || "Action effectuée");
      setMessages((m) => [...m, { role: "assistant", content: `✅ ${r.data?.message || "OK"}` }]);
      if (pending.tool === "adjust_stock_manual") openMovements(pending.args.user_id);
      setPending(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'exécution");
      setMessages((m) => [...m, { role: "assistant", content: `❌ ${e.response?.data?.detail || "Erreur technique"}` }]);
    } finally {
      setExecuting(false);
    }
  };

  const openMovements = async (uid) => {
    try {
      const r = await axios.get(`${API}/ceo/ai-assistant/stock-movements/${uid}`);
      setMessages((m) => [...m, { role: "card", kind: "get_stock_movements", data: r.data }]);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="ceo-ai-modal">
      <div className="bg-gradient-to-br from-zinc-950 via-fuchsia-950/30 to-cyan-950/30 border-2 border-fuchsia-500/50 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-[0_0_60px_rgba(217,70,239,0.35)]" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-fuchsia-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fuchsia-500 to-cyan-500 flex items-center justify-center shadow-lg">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-black text-lg">Ada · AI Pèsonèl CEO</h3>
              <p className="text-fuchsia-300 text-[10px] uppercase tracking-widest">Claude Sonnet 4.5 · zouti egzekitif</p>
            </div>
          </div>
          <div className="flex items-center gap-1 ml-auto mr-2">
            {[["chat", "Chat"], ["audit", "Journal d'audit"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`ceo-ai-tab-${k}`}
                className={`text-xs font-bold px-3 py-1.5 rounded-lg ${tab === k ? "bg-fuchsia-600 text-white" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>{l}</button>
            ))}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg" data-testid="close-ceo-ai">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Chat area */}
        {tab === "audit" ? <div className="flex-1 overflow-y-auto p-4"><AuditLog /></div> : (<>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ceo-ai-chat">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <p className="text-zinc-400 text-sm mb-3">Salye Kymbell 👋 M ka ede w kounye a. Eseye:</p>
              <div className="flex flex-col gap-2 max-w-md mx-auto">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => sendMessage(s)} data-testid={`ai-suggestion-${s.slice(0,10)}`}
                    className="text-left text-xs bg-zinc-900/60 hover:bg-fuchsia-900/30 border border-zinc-800 hover:border-fuchsia-500/40 rounded-lg px-3 py-2 text-zinc-300 transition-colors">
                    💡 {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => m.role === "card" ? (
            <div key={i} data-testid={`ai-card-${m.kind}-${i}`}>
              {m.kind === "get_stock_movements"
                ? <MovementsCard data={m.data} onPreview={setPending} />
                : <AnomaliesCard data={m.data} onOpenUser={openMovements} />}
            </div>
          ) : (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}-${i}`}>
              <div data-no-translate className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-fuchsia-600 text-white rounded-br-sm"
                  : "bg-zinc-800/80 text-zinc-100 rounded-bl-sm border border-zinc-700"
              }`}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-zinc-800/80 rounded-2xl rounded-bl-sm px-3 py-2 border border-zinc-700 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-fuchsia-400" />
                <span className="text-zinc-400 text-sm">Ada ap panse…</span>
              </div>
            </div>
          )}

          {/* Pending action confirm card */}
          {pending && (
            <div className="bg-gradient-to-br from-amber-950/70 to-orange-950/70 border-4 border-amber-400 rounded-xl p-4 my-2 animate-pulse-border shadow-[0_0_40px_rgba(251,191,36,0.5)]" data-testid="ai-pending-action" style={{animation: 'pulse-border 1.6s ease-in-out infinite'}}>
              <div className="bg-amber-500 text-black text-center font-black text-xs uppercase py-1 rounded-md mb-3 tracking-widest">⚠️ AKSYON POKO FÈT — TANN KONFIMASYON W</div>
              <div className="flex items-start gap-3 mb-3">
                <ShieldAlert className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <p className="text-amber-300 font-black text-sm uppercase tracking-wide">Konfimansyon Egzekitif</p>
                  <p className="text-amber-100 text-sm mt-1">{pending.summary}</p>
                  <p className="text-amber-400 text-[10px] uppercase mt-2 tracking-widest">Zouti: {pending.tool}</p>
                </div>
              </div>
              {(pending.changes || []).length > 0 && <ChangesTable changes={pending.changes} />}
              {pending.error && <p className="bg-rose-600 text-white text-xs font-bold rounded-md px-3 py-2 mb-3" data-testid="ai-pending-error">⛔ {pending.error}</p>}
              <div className="flex gap-2">
                <button
                  onClick={confirmAction}
                  disabled={executing || !!pending.error}
                  data-testid="ai-confirm-execute"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-black py-3 rounded-lg text-sm flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/40 ring-2 ring-emerald-400/40"
                >
                  {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {executing ? "Ap egzekite…" : "Konfime & Ekzekite"}
                </button>
                <button
                  onClick={() => { setPending(null); setMessages((m) => [...m, { role: "assistant", content: "OK — action annulée." }]); }}
                  disabled={executing}
                  data-testid="ai-cancel-action"
                  className="px-4 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold py-3 rounded-lg text-sm"
                >
                  Anile
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-3 border-t border-fuchsia-500/20 flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") sendMessage(); }}
            placeholder="Ex: Chèche Casty, sispann Jean, mete stock Kedjee a 20…"
            className="flex-1 bg-zinc-900/70 border border-zinc-700 focus:border-fuchsia-500 rounded-xl px-4 py-2.5 text-white text-sm outline-none"
            disabled={loading}
            data-testid="ceo-ai-input"
          />
          <button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            data-testid="ceo-ai-send"
            className="bg-gradient-to-r from-fuchsia-600 to-cyan-600 hover:from-fuchsia-500 hover:to-cyan-500 disabled:opacity-40 text-white px-4 rounded-xl flex items-center justify-center"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        </>)}
      </div>
    </div>
  );
}
